import type { ReactElement } from 'react';
import { Button, Text, makeStyles, tokens } from '@fluentui/react-components';
import { Compose20Regular } from '@fluentui/react-icons';
import { brandName } from '@gandhi/mail-protocol';
import { useBrand } from '@gandhi-group/ui';
import type { MailApiClient } from '../api/client.js';
import { useController, useMail } from '../state/context.js';
import { BrandChangeDialog } from './BrandChangeDialog.js';
import { CommandBar } from './CommandBar.js';
import { Composer } from './Composer.js';
import { FolderTree } from './FolderTree.js';
import { MailboxSwitcher } from './MailboxSwitcher.js';
import { MessageList } from './MessageList.js';
import { ReadingPane } from './ReadingPane.js';
import { UndoSendBar } from './UndoSendBar.js';
import { useShortcuts } from './useShortcuts.js';
import type { RichTextCommands } from './RichTextEditor.js';

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    height: '100vh',
    background: tokens.colorNeutralBackground2,
  },
  body: {
    display: 'grid',
    gridTemplateColumns: '256px 380px 1fr',
    minHeight: 0,
    '@media (max-width: 1100px)': { gridTemplateColumns: '360px 1fr' },
    '@media (max-width: 760px)': { gridTemplateColumns: '1fr' },
  },
  rail: {
    overflowY: 'auto',
    background: tokens.colorNeutralBackground3,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1100px)': { display: 'none' },
  },
  compose: { padding: '12px 12px 4px' },
  list: {
    display: 'grid',
    // Both are needed: the implicit column is `auto`, so without minmax the list
    // grows to its widest row and slides under the reading pane.
    gridTemplateColumns: 'minmax(0, 1fr)',
    minHeight: 0,
    minWidth: 0,
    background: tokens.colorNeutralBackground1,
    borderInlineStartWidth: '1px',
    borderInlineEndWidth: '1px',
    borderInlineStartStyle: 'solid',
    borderInlineEndStyle: 'solid',
    borderInlineStartColor: tokens.colorNeutralStroke2,
    borderInlineEndColor: tokens.colorNeutralStroke2,
    '@media (max-width: 760px)': { '&[data-collapsed="true"]': { display: 'none' } },
  },
  pane: {
    position: 'relative',
    minHeight: 0,
    minWidth: 0,
    background: tokens.colorNeutralBackground1,
    '@media (max-width: 760px)': { '&[data-collapsed="true"]': { display: 'none' } },
  },
  dock: {
    position: 'absolute',
    insetBlockStart: '12px',
    insetBlockEnd: 0,
    insetInlineEnd: '12px',
    insetInlineStart: 'auto',
    display: 'flex',
    width: 'min(700px, calc(100% - 24px))',
    zIndex: 2,
  },
  errorText: { padding: '8px 16px', color: tokens.colorPaletteRedForeground1 },
});

export interface AppShellProps {
  readonly api: MailApiClient;
  readonly commands?: RichTextCommands;
  /** Forget the password and close the mailboxes. */
  readonly onSignOut: () => void;
}

/**
 * Brand chrome across the whole window, not a badge in a corner: the accent runs along
 * the top edge, down the mailbox rail and around compose. The test is whether you can
 * name the brand from three metres away without reading.
 */
export function AppShell(props: AppShellProps): ReactElement {
  const styles = useStyles();
  const controller = useController();
  const { accent, brand } = useBrand();
  const draft = useMail((s) => s.draft);
  const error = useMail((s) => s.error);
  const threadOpen = useMail((s) => s.activeThread !== null);
  const paneOpen = threadOpen || draft !== null;
  useShortcuts();

  return (
    <div className={styles.root} data-brand={brand} data-testid="app-shell">
      <CommandBar onSignOut={props.onSignOut} />

      <div className={styles.body}>
        <aside className={styles.rail} style={{ borderInlineStart: `4px solid ${accent}` }}>
          <div className={styles.compose}>
            <Button
              appearance="primary"
              icon={<Compose20Regular />}
              onClick={() => controller.composeNew()}
            >
              New message
            </Button>
          </div>
          <MailboxSwitcher />
          <FolderTree />
        </aside>

        <div className={styles.list} data-collapsed={paneOpen ? 'true' : 'false'}>
          <MessageList />
        </div>

        <main
          className={styles.pane}
          data-collapsed={paneOpen ? 'false' : 'true'}
          aria-label={`${brandName(brand)} reading and compose`}
        >
          {error !== null && (
            <Text className={styles.errorText} role="alert">
              {error}
            </Text>
          )}
          <ReadingPane api={props.api} />
          {draft !== null && (
            <div className={styles.dock}>
              <Composer {...(props.commands === undefined ? {} : { commands: props.commands })} />
            </div>
          )}
        </main>
      </div>

      <BrandChangeDialog />
      <UndoSendBar />
    </div>
  );
}
